import type {
  StrategicTerritoriesWidgetSchemaType,
  Territory,
} from '@inference.engine/widgets/web';
import { useTranslation } from 'react-i18next';

const MAX_PREVIEW_TERRITORIES = 3;

interface Props {
  data: StrategicTerritoriesWidgetSchemaType;
}

/**
 * Store-free, prop-only preview of a strategic-territories widget. Renders
 * props.data only — never touches the widget store or export deps.
 *
 * Two shapes flow through here: the live chat-flow widget holds a LIST of
 * territories, while a saved artifact is a single-territory snapshot
 * (`{ title, items: [territory] }`, keyed `saved-strategic-territories:…:i`).
 * A single territory gets a focused card that headlines its `keyMessage` — the
 * line `TerritoryCard` leads with — rather than reading as a one-row list.
 */
export const StrategicTerritoriesThumbnail = (props: Props) => {
  const { t } = useTranslation('analysis');
  const items = Array.isArray(props.data.items) ? props.data.items : [];
  const title = props.data.title || t('strategicTerritoriesWidget.strategy');

  if (items.length === 1) {
    return <SingleTerritoryPreview title={title} territory={items[0]} />;
  }

  const previewItems = items.slice(0, MAX_PREVIEW_TERRITORIES);
  return (
    <div className="flex h-full flex-col gap-2 overflow-hidden">
      <p className="truncate text-sm font-semibold text-[#1A1A1A]">{title}</p>
      <ul className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-hidden">
        {previewItems.map((territory) => (
          <li
            key={territory.id}
            className="flex flex-col gap-0.5 rounded-md border border-[#E5E5E7] p-1.5"
          >
            <div className="flex items-center gap-1">
              {territory.isRecommended && <RecommendedDot />}
              <p className="line-clamp-1 text-xs font-medium text-[#1A1A1A]">{territory.title}</p>
            </div>
            {territory.summary && (
              <p className="line-clamp-2 text-[10px] leading-snug text-[#6B6B6F]">
                {territory.summary}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Focused preview of a single saved territory: strategy title overline, the
 * territory title (with Recommended marker), its `keyMessage` as the hero line,
 * and a clamped `summary` for supporting context.
 */
function SingleTerritoryPreview({ title, territory }: { title: string; territory: Territory }) {
  return (
    <div className="flex h-full flex-col gap-1.5 overflow-hidden">
      <p className="truncate text-[10px] font-medium uppercase tracking-wide text-[#9A9A9F]">
        {title}
      </p>
      <div className="flex items-center gap-1">
        {territory.isRecommended && <RecommendedDot />}
        <p className="line-clamp-2 text-sm font-semibold text-[#1A1A1A]">{territory.title}</p>
      </div>
      {territory.keyMessage && (
        <p className="line-clamp-2 text-xs font-medium italic text-[#1A1A1A]">
          “{territory.keyMessage}”
        </p>
      )}
      {territory.summary && (
        <p className="line-clamp-4 min-h-0 flex-1 text-[10px] leading-snug text-[#6B6B6F]">
          {territory.summary}
        </p>
      )}
    </div>
  );
}

function RecommendedDot() {
  const { t } = useTranslation('analysis');

  return (
    <span
      className="size-1.5 shrink-0 rounded-full bg-orange-500"
      aria-label={t('strategicTerritoriesWidget.strategicTerritoriesThumbnail.recommended')}
    />
  );
}
